import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { servicesData, companyInfo } from '../data';

const Services = () => {
  const [activeService, setActiveService] = useState(null);
  const services = servicesData || [];

  return (
    <>
      <Helmet>
        <title>Our Services | Al Abeer Diamond</title>
        <meta name="description" content={`Explore our comprehensive range of services including civil construction, electrical works, mechanical works, manpower supply, heavy machinery rental, and cleaning solutions.`} />
        <meta name="keywords" content="construction services saudi arabia, civil construction, electrical works, mechanical works, manpower supply, heavy machinery rental" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[40vh] flex items-center overflow-hidden bg-secondary">
        <div className="absolute inset-0 bg-hero-gradient opacity-90"></div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <span className="inline-block px-4 py-2 bg-primary/20 text-primary-light rounded-full text-sm font-medium font-oswald tracking-wider mb-4">
              What We Offer
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white font-oswald leading-tight">
              Comprehensive <br />
              <span className="text-primary-light">Solutions</span>
            </h1>
            <p className="text-gray-300 text-lg mt-4 max-w-2xl">
              Combining engineering expertise, skilled manpower, and modern technology to deliver projects on time—every time.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <span className="section-badge">Our Expertise</span>
              <h2 className="heading-primary mt-4">
                What <span className="text-gradient">We Do</span>
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto mt-4">
                We provide a comprehensive range of professional services designed to meet the diverse needs of our clients.
              </p>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl overflow-hidden shadow-soft card-hover border border-gray-100"
              >
                <div className="relative h-56 overflow-hidden">
                  <img 
                    src={service.image || `https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?w=600&q=80`} 
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-secondary/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-6">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
                        <i className={`${service.icon} text-2xl text-white`}></i>
                      </div>
                      <h3 className="text-xl font-bold text-white font-oswald">
                        {service.title}
                      </h3>
                    </div>
                  </div>
                </div>
                
                <div className="p-6">
                  <p className="text-gray-600 text-sm mb-4">
                    {service.description}
                  </p>
                  
                  <button
                    onClick={() => setActiveService(activeService === service.id ? null : service.id)}
                    className="text-primary font-medium hover:text-primary-dark transition-colors text-sm flex items-center"
                  >
                    {activeService === service.id ? 'Hide Details' : 'View All Services'}
                    <i className={`uil uil-angle-${activeService === service.id ? 'up' : 'down'} ml-1`}></i>
                  </button>
                  
                  {activeService === service.id && service.features && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      transition={{ duration: 0.3 }}
                      className="mt-4 pt-4 border-t border-gray-100"
                    >
                      <h4 className="font-oswald font-semibold text-secondary mb-3">Our Services Include:</h4>
                      <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start text-sm text-gray-600">
                            <i className="uil uil-check-circle text-primary mr-2 mt-0.5"></i>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </motion.div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Our Services */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <span className="section-badge">Why Choose Us</span>
              <h2 className="heading-primary mt-4">
                Why Choose <span className="text-gradient">Our Services</span>
              </h2>
              <p className="text-gray-600 leading-relaxed mt-4">
                At Al Abeer Diamond, we combine engineering expertise, skilled manpower, and modern technology to deliver projects on time—every time—without compromising on quality or safety.
              </p>
              
              <div className="space-y-4 mt-6">
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="uil uil-check-circle text-primary"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-secondary">Quality Assurance</h4>
                    <p className="text-gray-600 text-sm">High-quality materials and workmanship with full compliance with safety standards.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="uil uil-clock text-primary"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-secondary">On-Time Delivery</h4>
                    <p className="text-gray-600 text-sm">Reliable execution with transparent and professional project management.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="uil uil-users-alt text-primary"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-secondary">Expert Team</h4>
                    <p className="text-gray-600 text-sm">Skilled engineers, technicians, and construction professionals for every project.</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-hover">
                <img 
                  src="https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?w=800&q=80" 
                  alt="Why Choose Al Abeer Diamond"
                  className="w-full h-auto"
                />
              </div>
              <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-primary/10 rounded-full -z-10"></div>
              <div className="absolute -top-4 -right-4 w-16 h-16 bg-primary/20 rounded-full -z-10"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-primary">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white font-oswald">
              Need a Custom Solution?
            </h2>
            <p className="text-white/80 text-lg mt-4 max-w-2xl mx-auto">
              Contact us to discuss your specific requirements
            </p>
            <Link to="/contact" className="inline-block mt-6 bg-white text-primary px-8 py-3 rounded-full font-medium hover:shadow-lg transition-all hover:-translate-y-1">
              Contact Us <i className="uil uil-arrow-right ml-2"></i>
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Services;